import tippy from 'tippy.js';
import type { Instance, Props } from 'tippy.js';

type TooltipWindow = Window & {
  JidokaTooltips?: TooltipService;
};

type TooltipService = {
  refresh: (root?: ParentNode) => void;
  enable: () => void;
  disable: () => void;
};

const initializedTooltips = new WeakMap<Element, Instance<Props>>();
let enabled = true;
let observer: MutationObserver | null = null;

function getTooltipContent(element: Element): string | null {
  const explicit = element.getAttribute('data-tippy-content');
  if (explicit) {
    return explicit;
  }

  const originalTitle = element.getAttribute('data-original-title');
  if (originalTitle) {
    element.setAttribute('data-tippy-content', originalTitle);
    return originalTitle;
  }

  const title = element.getAttribute('title');
  if (title) {
    element.setAttribute('data-tippy-content', title);
    element.setAttribute('data-original-title', title);
    element.removeAttribute('title');
    return title;
  }

  return null;
}

function refresh(root: ParentNode = document): void {
  const elements = Array.from(root.querySelectorAll<HTMLElement>('[data-tippy-content]'));

  elements.forEach((element) => {
    const tooltipElement = element as HTMLElement & { _tippy?: unknown };
    if (initializedTooltips.has(element) || element.closest('.nav-wrapper') || tooltipElement._tippy) {
      return;
    }

    const content = getTooltipContent(element);
    if (!content) {
      return;
    }

    const placement = element.getAttribute('data-tippy-placement') || element.getAttribute('data-placement') || 'top';
    const delayAttribute = element.getAttribute('data-tippy-delay');
    const delay = delayAttribute
      ? delayAttribute.split(',').map(value => Number.parseInt(value.trim(), 10))
      : undefined;
    const instance = tippy(element, {
      content,
      allowHTML: true,
      placement: placement as Props['placement'],
      ...(delay && delay.length > 0
        ? { delay: (delay.length === 1 ? delay[0] : [delay[0], delay[1] ?? 0]) as Props['delay'] }
        : {}),
      onShow() {
        if (!enabled) {
          return false;
        }
      }
    });

    initializedTooltips.set(element, instance);
  });
}

function startObserver(): void {
  if (observer) {
    return;
  }

  observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) {
          refresh(node);
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

export function initTooltipAdapter(): void {
  const service: TooltipService = {
    refresh,
    enable() {
      enabled = true;
    },
    disable() {
      enabled = false;
    }
  };

  (window as TooltipWindow).JidokaTooltips = service;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      refresh();
      startObserver();
    }, { once: true });
    return;
  }

  refresh();
  startObserver();
}
